import React, { useEffect, useState } from 'react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../firebase';
import { useCompanySession } from '../session';
import CompanyLayout from '../components/CompanyLayout';
import { defaultWallets, PULSE_PAY_DISPLAY, PULSE_WAVE_DISPLAY, walletsFromTenant } from '../wallets';
import { hasWalletRails, railEnabled, useTenantFeatures } from '../tenantFeatures';
import { useI18n } from '../i18n';

function CompanySettings() {
    const { session, loading } = useCompanySession();
    const { t } = useI18n();
    const features = useTenantFeatures(session && session.tenantId);
    const [logoUrl, setLogoUrl] = useState('');
    const [wallets, setWallets] = useState(defaultWallets());
    const [busy, setBusy] = useState(false);
    const [message, setMessage] = useState('');

    useEffect(() => {
        if (!session?.tenantId) return;
        getDoc(doc(db, 'tenants', session.tenantId)).then((snap) => {
            if (!snap.exists()) return;
            setLogoUrl(snap.data().logoUrl || '');
            setWallets(walletsFromTenant(snap.data()));
        });
    }, [session]);

    const saveWallets = async (e) => {
        e.preventDefault();
        setBusy(true);
        setMessage('');
        try {
            const current = walletsFromTenant({ wallets });
            const next = {
                pulsePayNumber: current.pulsePayNumber || PULSE_PAY_DISPLAY,
                waveNumber: current.waveNumber || PULSE_WAVE_DISPLAY,
                orangeNumber: current.orangeNumber || '',
                qmoneyNumber: current.qmoneyNumber || '',
                commissionRateBroker: Number(wallets.commissionRateBroker) || 0.1
            };
            if (railEnabled(features, 'pulsepay')) next.pulsePayNumber = wallets.pulsePayNumber || PULSE_PAY_DISPLAY;
            if (railEnabled(features, 'wave')) next.waveNumber = wallets.waveNumber || PULSE_WAVE_DISPLAY;
            if (railEnabled(features, 'orange')) next.orangeNumber = wallets.orangeNumber || '';
            if (railEnabled(features, 'qmoney')) next.qmoneyNumber = wallets.qmoneyNumber || '';
            await setDoc(doc(db, 'tenants', session.tenantId), {
                wallets: next,
                updatedAt: new Date().toISOString()
            }, { merge: true });
            setWallets({ ...wallets, ...next });
            setMessage(t('settings.walletsSaved'));
        } catch (err) {
            setMessage(err.message || t('settings.walletsFailed'));
        }
        setBusy(false);
    };

    const handleUpload = async (e) => {
        const file = e.target.files && e.target.files[0];
        if (!file || !session) return;
        if (!file.type.startsWith('image/')) {
            setMessage('Please choose an image file (PNG, JPG, or SVG).');
            return;
        }
        setBusy(true);
        setMessage('');
        try {
            const ext = (file.name.split('.').pop() || 'png').toLowerCase();
            const path = 'logos/' + session.tenantId + '/logo.' + ext;
            const fileRef = ref(storage, path);
            await uploadBytes(fileRef, file, { contentType: file.type });
            const url = await getDownloadURL(fileRef);
            await setDoc(doc(db, 'tenants', session.tenantId), {
                logoUrl: url,
                logoPath: path,
                updatedAt: new Date().toISOString()
            }, { merge: true });
            await setDoc(doc(db, 'directory', session.tenantId), {
                companyName: session.companyName,
                logoUrl: url
            }, { merge: true });
            setLogoUrl(url);
            setMessage('Business logo updated. It will appear on your dashboard and client policy view.');
        } catch (err) {
            const hint = /storage|bucket|404|403/i.test(err.message || '')
                ? ' Firebase Storage may not be started yet. In the Firebase console open Storage → Get Started, then try again.'
                : '';
            setMessage((err.message || 'Upload failed.') + hint);
        }
        setBusy(false);
        e.target.value = '';
    };

    if (loading || !session) return <div className="p-5">{t('settings.loading')}</div>;
    if (session.role !== 'admin') {
        return (
            <CompanyLayout session={session}>
                <div className="alert alert-warning">{t('settings.adminOnly')}</div>
            </CompanyLayout>
        );
    }

    const walletsOn = hasWalletRails(features);
    const showPulse = railEnabled(features, 'pulsepay');
    const showWave = railEnabled(features, 'wave');
    const showOrange = railEnabled(features, 'orange');
    const showQmoney = railEnabled(features, 'qmoney');

    return (
        <CompanyLayout session={session} logoUrl={logoUrl}>
            <h3 className="mb-3">{t('settings.title')}</h3>
            <p className="text-muted">{walletsOn ? t('settings.intro', { company: session.companyName, wave: PULSE_PAY_DISPLAY }) : t('settings.introCash')}</p>
            {message && <div className="alert alert-info">{message}</div>}
            <div className="card border-0 shadow-sm mb-4">
                <div className="card-body">
                    <h6>{t('settings.logo')}</h6>
                    <div className="d-flex align-items-center gap-3 mb-3">
                        {logoUrl ? (
                            <img src={logoUrl} alt="Company logo" style={{ height: 64, maxWidth: 180, objectFit: 'contain' }} />
                        ) : (
                            <div className="text-muted">{t('settings.noLogo')}</div>
                        )}
                    </div>
                    <input type="file" accept="image/*" className="form-control" disabled={busy} onChange={handleUpload} />
                    {busy && <div className="small text-muted mt-2">Uploading...</div>}
                </div>
            </div>
            {walletsOn ? (
                <form className="card border-0 shadow-sm" onSubmit={saveWallets}>
                    <div className="card-body">
                        <h6>{t('settings.wallets')}</h6>
                        <p className="small text-muted">{t('settings.walletsHint', { wave: PULSE_PAY_DISPLAY })}</p>
                        <div className="row g-2">
                            {showPulse && (
                                <div className="col-md-6">
                                    <label className="form-label small">{t('settings.pulsePay')}</label>
                                    <input className="form-control" value={wallets.pulsePayNumber || ''} onChange={(e) => setWallets({ ...wallets, pulsePayNumber: e.target.value })} placeholder="+220 795 9000" />
                                </div>
                            )}
                            {showWave && (
                                <div className="col-md-6">
                                    <label className="form-label small">{t('settings.wave')}</label>
                                    <input className="form-control" value={wallets.waveNumber} onChange={(e) => setWallets({ ...wallets, waveNumber: e.target.value })} placeholder="+220 795 9000" />
                                </div>
                            )}
                            {showOrange && (
                                <div className="col-md-6">
                                    <label className="form-label small">{t('settings.orange')}</label>
                                    <input className="form-control" value={wallets.orangeNumber} onChange={(e) => setWallets({ ...wallets, orangeNumber: e.target.value })} placeholder="Optional" />
                                </div>
                            )}
                            {showQmoney && (
                                <div className="col-md-6">
                                    <label className="form-label small">{t('settings.qmoney')}</label>
                                    <input className="form-control" value={wallets.qmoneyNumber} onChange={(e) => setWallets({ ...wallets, qmoneyNumber: e.target.value })} placeholder="Optional" />
                                </div>
                            )}
                            <div className="col-md-6">
                                <label className="form-label small">{t('settings.commission')}</label>
                                <input type="number" min="0" max="30" step="1" className="form-control" value={Math.round((wallets.commissionRateBroker || 0.1) * 100)} onChange={(e) => setWallets({ ...wallets, commissionRateBroker: Number(e.target.value) / 100 })} />
                            </div>
                        </div>
                        <button className="btn btn-dark mt-3" type="submit" disabled={busy}>{t('settings.saveWallets')}</button>
                    </div>
                </form>
            ) : (
                <div className="card border-0 shadow-sm">
                    <div className="card-body">
                        <h6>{t('settings.wallets')}</h6>
                        <p className="small text-muted mb-3">{t('settings.walletsDisabled')}</p>
                        <form onSubmit={saveWallets}>
                            <label className="form-label small">{t('settings.commission')}</label>
                            <input type="number" min="0" max="30" step="1" className="form-control" style={{ maxWidth: 200 }} value={Math.round((wallets.commissionRateBroker || 0.1) * 100)} onChange={(e) => setWallets({ ...wallets, commissionRateBroker: Number(e.target.value) / 100 })} />
                            <button className="btn btn-dark mt-3" type="submit" disabled={busy}>{t('settings.saveWallets')}</button>
                        </form>
                    </div>
                </div>
            )}
            <div className="alert alert-light border mt-4 mb-0">
                {t('settings.licenceNote')}
            </div>
        </CompanyLayout>
    );
}

export default CompanySettings;
